/**
 * Filters for browser-side Sentry events that are not app faults.
 */

/**
 * Every frame's filename in a Sentry event, in stack order.
 * @param {object} event
 * @returns {string[]}
 */
const eventFrameFilenames = (event) => {
  const values = event?.exception?.values

  if (!Array.isArray(values)) {
    return []
  }

  return values
    .flatMap(value => value?.stacktrace?.frames || [])
    .map(frame => frame?.filename)
    .filter(filename => typeof filename === 'string' && filename !== '')
}

/**
 * True when the whole stack is attributed to an HTML document rather than to one
 * of the app's script bundles.
 *
 * The app is served as hashed `/js/*.js` chunks, so a genuine fault always has at
 * least one `.js` frame. A stack made only of document URLs comes from code the
 * browser injected into the page — Chrome for iOS's Google Translate bundle is
 * the loud one, and it reports as in-app because the injected script inherits
 * the document URL. The three tiny inline scripts in index.template.html are
 * also dropped by this, which is an accepted trade for silencing that class.
 *
 * @param {object} event Sentry event, as handed to `beforeSend`
 * @returns {boolean}
 */
export const isInjectedPageScriptError = (event) => {
  const filenames = eventFrameFilenames(event)

  if (!filenames.length) {
    return false
  }

  return !filenames.some(filename => filename.split(/[?#]/)[0].endsWith('.js'))
}

const INTERCOM_SCRIPT_ORIGIN = 'https://js.intercomcdn.com/'

const NATIVE_FRAME_FILENAMES = ['<anonymous>', '[native code]']

/**
 * Filename of the top script frame — the one that threw — skipping native frames.
 * @param {object} event
 * @returns {string|undefined}
 */
const thrownFromFilename = event => eventFrameFilenames(event)
  .filter(filename => !NATIVE_FRAME_FILENAMES.includes(filename))
  .pop()

/**
 * True when the frame that threw is in Intercom's Messenger script. Its page
 * listeners run through the app's Sentry wrappers, so its errors arrive as ours.
 *
 * Only the top script frame counts: an app fault reached from an Intercom
 * callback still has an app frame on top and is kept.
 *
 * @param {object} event Sentry event, as handed to `beforeSend`
 * @returns {boolean}
 */
export const isIntercomWidgetError = (event) => {
  return thrownFromFilename(event)?.startsWith(INTERCOM_SCRIPT_ORIGIN) === true
}

// Extension URL schemes, plus the `webpack://<name>-extension/` sourceURL some
// extensions (Jam) stamp on their injected scripts.
const BROWSER_EXTENSION_FILENAME = /^((chrome|moz|safari|safari-web|ms-browser)-extension:\/\/|webpack:\/\/[^/]*-extension\/)/

/**
 * True when the frame that threw is in a browser extension's injected script.
 * Extensions that wrap `history`/`fetch` run inside the app's Sentry wrappers,
 * so their errors arrive as ours. Same top-frame rule as the Intercom filter.
 *
 * @param {object} event Sentry event, as handed to `beforeSend`
 * @returns {boolean}
 */
export const isBrowserExtensionError = (event) => {
  return BROWSER_EXTENSION_FILENAME.test(thrownFromFilename(event) || '')
}
